import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { faShieldCheck } from '@fortawesome/pro-solid-svg-icons/faShieldCheck';
import { useEffect, useState } from 'react';

import { tests } from 'us-react-components/dist/constants/Tests';
import { BetaBadge } from 'us-react-components/dist/BetaBadge/BetaBadge';
import { InfoCard } from 'us-react-components/dist/InfoCard/InfoCard';
import { NuxContainer } from 'us-react-components/dist/NuxContainer/NuxContainer';
import { AiService } from 'us-web-services';
import { Button, ButtonSize, ButtonType } from '../../../components/Button/Button';
import { useDependencies } from '../../../dependencies';
import careHand from '../../../images/animations/care_hand.json';
import { LottieComponent, LottieComponentProps } from '../../../types/LottieComponent';
import { Routes } from '../../../Routes';

export const GettingStarted: LottieComponent<LottieComponentProps> = observer((props: LottieComponentProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { useLottie } = props;

  const { stores: { familyRegistration, parent, user }, services: { tracker } } = useDependencies();
  const [isAiAvailable, setIsAiAvailable] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const checkAiStatus = async (): Promise<void> => {
      if (!user.inTestGroup(tests.AI_NUX_TESTER)) {
        return;
      }

      await parent.refreshParent();

      if (cancelled || parent.parent?.companyEmployee) {
        return;
      }

      try {
        const aiStatus = (await AiService.getStatus()).data?.data;
        if (!cancelled) {
          setIsAiAvailable(aiStatus?.available ?? false);
        }
      } catch {
        if (!cancelled) {
          setIsAiAvailable(false);
        }
      }
    };

    void checkAiStatus();

    return (): void => {
      cancelled = true;
    };
  }, []);

  familyRegistration.setCurrentRoute(Routes.parent.registration.gettingStarted);

  const onContinue = async (): Promise<void> => {
    const trackingProps = {
      elementId: 'getting-started-continue',
      elementText: isAiAvailable ? t('pages.nux.family.gettingStarted.continueNoAiCta') : t('pages.nux.family.gettingStarted.continueCta')
    };

    await tracker.track('clicked-button', trackingProps);

    navigate(Routes.parent.registration.vertical);
  }

  const renderInfoCard = (): JSX.Element => (
    <InfoCard
      iconName={faShieldCheck}
      inverted
      title={t('pages.nux.family.fantastic.infoCard.title')}
      description={t('pages.nux.family.fantastic.infoCard.description')}
    />
  );

  const options = {
    animationData: careHand,
    loop: false,
    style: {
      margin: '5px auto 0 auto',
      height: '237px',
      width: '237px',
    }
  };

  const { View } = useLottie(options);

  return (
    <NuxContainer
      inverted
      hideProgress
      familyRegistration={familyRegistration}
      navigate={navigate}
      tracker={tracker}
      infoCard={renderInfoCard()}
      title={t('pages.nux.family.gettingStarted.title')}
      onContinue={onContinue}
      continueTitle={isAiAvailable ? t('pages.nux.family.gettingStarted.continueNoAiCta') : t('pages.nux.family.gettingStarted.continueCta')}
    >
      <div className='text-center'>
          <div className='h-[80px] mt-3 mx-2'>
          {isAiAvailable && (
            <>
              <Button
                id='getting-started-ai-cta'
                className='outline mb-2'
                buttonType={ButtonType.primary}
                buttonSize={ButtonSize.wide}
                onClick={() => navigate(Routes.parent.registration.assistant)}
              >
                {t('pages.nux.family.gettingStarted.aiCta')}
              </Button>
              <div className='flex items-center gap-2 max-w-[18rem] md:max-w-none md:mt-1 mx-auto'>
                <BetaBadge label={t('components.betaBadge.label')} className='shrink-0' />
                <p className='text-sm text-gray-200 text-left leading-tight'>
                  {t('pages.nux.family.gettingStarted.aiDisclaimer')}
                </p>
              </div>
            </>
          )}
          </div>
        {View}
      </div>
    </NuxContainer>
  )
});
